import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AttendanceTable from '../Attendancecomponents/AttendanceTable';
import { Title } from '../../UI/Typography';
import { FlexContainer, Container } from '../../UI/Containers';
import { Input } from '../../UI/Inputs';
import { Text } from '../../UI/Typography';
import { getAttendanceByDate, updateAttendance, editAttendanceTimes } from '../../../Services/Api/attendanceService'; 
import { getAllSites } from '../../../Services/Api/siteService'; 
import { useAuth } from '../../../Context/AuthContext'; 

const PageContainer = styled(Container)`
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.background};
  max-width: 1400px;
  margin: 0 auto;
`;


const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  > h1, > h2, > h3, > h4, > h5, > p { margin: 0; }
`;

const DateInput = styled(Input)`
  width: auto;
  min-width: 150px;
  height: 45px;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  line-height: normal;
  box-sizing: border-box;
  vertical-align: middle;
  
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
    }
  }
`;

const StatusMessage = styled(Text)`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => theme.spacing.md} 0;
  text-align: center;
  
  ${({ variant, theme }) => variant === 'loading' && `
    background-color: ${theme.colors.primary}15;
    color: ${theme.colors.primary};
    border: 1px solid ${theme.colors.primary}30;
  `}
  
  ${({ variant, theme }) => variant === 'error' && `
    background-color: ${theme.colors.error}15;
    color: ${theme.colors.error};
    border: 1px solid ${theme.colors.error}30;
  `}
`;

const DateControlsContainer = styled(FlexContainer)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    align-items: stretch;
    
    & > * {
      width: 100%;
      text-align: center;
    }
  }
`;

const SummaryRow = styled(FlexContainer)`
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.sm};
    background: ${({ theme }) => theme.colors.backgroundSecondary || '#fff'};
    border: 1px solid ${({ theme }) => theme.colors.border};
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    min-width: 140px;
    text-align: center;
    white-space: nowrap;
    font-size: 14px;
  }
  
  & > .present {
    background-color: ${({ theme }) => `${theme.colors.success}15`};
    border-color: ${({ theme }) => `${theme.colors.success}30`};
    color: ${({ theme }) => theme.colors.success};
  }
  & > .late {
    background-color: ${({ theme }) => `${theme.colors.warning || '#f59e0b'}15`};
    border-color: ${({ theme }) => `${theme.colors.warning || '#f59e0b'}30`};
    color: ${({ theme }) => theme.colors.warning || '#f59e0b'};
  }
  & > .absent {
    background-color: ${({ theme }) => `${theme.colors.error}15`};
    border-color: ${({ theme }) => `${theme.colors.error}30`};
    color: ${({ theme }) => theme.colors.error};
  }
  & > .leaves {
    background-color: ${({ theme }) => `${theme.colors.primary}15`};
    border-color: ${({ theme }) => `${theme.colors.primary}30`};
    color: ${({ theme }) => theme.colors.primary};
  }

`;


const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0'); 
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function AttendanceMain() {
  const [attendanceData, setAttendanceData] = useState([]); 
  const [sites, setSites] = useState([]); // State for site boundaries
  const [isLoading, setIsLoading] = useState(false); 
  const [isUpdating, setIsUpdating] = useState(false); 
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getTodayDateString()); 
  const { user } = useAuth(); 

  // Function to fetch attendance data
  const fetchAttendance = async (date) => {
    setIsLoading(true);
    setError(null);
    // Don't clear data immediately if just refreshing after update
    // setAttendanceData([]); 

    try {
      console.log(`Fetching attendance for date: ${date}`);
      const data = await getAttendanceByDate(date); 
      setAttendanceData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch attendance data.');
      console.error("Fetching attendance failed:", err);
      setAttendanceData([]); // Clear data on fetch error
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch site boundaries
  const fetchSites = async () => {
    // Don't necessarily need setIsLoading here unless it's slow
    try {
      console.log("Fetching site boundaries...");
      const fetchedSites = await getAllSites();
      setSites(fetchedSites);
      console.log("Site boundaries fetched:", fetchedSites);
    } catch (err) {
      // Handle error fetching sites separately or combine
      console.error("Fetching sites failed:", err);
      setError(prev => prev ? `${prev}\nFailed to fetch site boundaries.` : 'Failed to fetch site boundaries.');
    }
  };

  // Fetch data effect
  useEffect(() => {
    if (selectedDate) {
      fetchAttendance(selectedDate);
    }
    // Fetch sites only once on mount (or if they might change)
    fetchSites(); 
  }, [selectedDate]); 

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  // Handler for notes submission from the modal via the table
  const handleNotesSubmit = async (itemId, notes) => {
    console.log(`[handleNotesSubmit] Received itemId: ${itemId}, notes: "${notes}"`); // Log received data

    const itemToUpdate = attendanceData.find(item => item.id === itemId);
    if (!itemToUpdate) {
      console.error("[handleNotesSubmit] Item not found for update:", itemId);
      setError("Could not find the record to update.");
      return;
    }
    console.log("[handleNotesSubmit] Found item to update:", itemToUpdate);

    const acceptedByUsername = user?.username; 
    console.log("[handleNotesSubmit] Logged in username from context:", acceptedByUsername); // Log username
    
    if (!acceptedByUsername) {
        console.error("[handleNotesSubmit] Logged in user's username not found in AuthContext.");
        setError("Could not identify the user performing the action. Please log in again.");
        return; 
    }

    const updateData = {
      username: itemToUpdate.username, 
      date: itemToUpdate.date,         
      accepted: true,                  
      notes: notes,                    
      accepted_by: acceptedByUsername  
    };

    console.log("[handleNotesSubmit] Preparing to send updateData:", updateData); // Log the exact payload
    
    setIsUpdating(true); 
    setError(null); 

    try {
      console.log("[handleNotesSubmit] Calling updateAttendance service...");
      const result = await updateAttendance(updateData); // Capture result
      console.log("[handleNotesSubmit] updateAttendance successful, result:", result); // Log success result
      
      console.log("[handleNotesSubmit] Re-fetching attendance data...");
      await fetchAttendance(selectedDate); // Re-fetch data
      console.log("[handleNotesSubmit] Re-fetch complete.");

    } catch (err) {
      // This block will execute if updateAttendance throws an error
      console.error("[handleNotesSubmit] Error during updateAttendance call:", err); // Log the caught error
      setError(err.message || "Failed to save notes."); 
    } finally {
      console.log("[handleNotesSubmit] Setting isUpdating to false.");
      setIsUpdating(false); 
    }
  };

  return (
    <PageContainer style={{ padding: 0, }}>
      <DateControlsContainer justify="space-between" align="center" responsive>
        <FlexContainer align="center" gap="md">
          <TitleWrapper>
            <Title style={{ margin: 10 }}>
              Attendance Records
            </Title>
            <SummaryRow>
              <div className="present">
                <Text size="sm" weight="semibold">Present</Text>
                <Text size="lg">{attendanceData.filter(a => a.status === 'present' || a.status === 'late').length}</Text>
              </div>
              <div className="absent">
                <Text size="sm" weight="semibold">Absent</Text>
                <Text size="lg">{attendanceData.filter(a => a.status === 'absent').length}</Text>
              </div>
              <div className="late">
                <Text size="sm" weight="semibold">Late</Text>
                <Text size="lg">{attendanceData.filter(a => a.status === 'late').length}</Text>
              </div>
              <div className="leaves">
                <Text size="sm" weight="semibold">Leaves</Text>
                <Text size="lg">{attendanceData.filter(a => a.status === 'leave' || a.status === 'half-day').length}</Text>
              </div>
            </SummaryRow>
          </TitleWrapper>
        </FlexContainer>
        <FlexContainer align="center" gap="sm">
          <DateInput 
            type="date" 
            id="attendance-date"
            value={selectedDate} 
            onChange={handleDateChange}
            title="Select date to view attendance records"
          />
        </FlexContainer>
      </DateControlsContainer>
      
      {/* Display loading/updating status */}
      {(isLoading || isUpdating) && (
        <StatusMessage variant="loading" size="sm">
          {isLoading ? 'Loading attendance data...' : 'Updating records...'}
        </StatusMessage>
      )}
      
      {/* Display error message */}
      {error && (
        <StatusMessage variant="error" size="sm">
          {error}
        </StatusMessage>
      )}
      
      {/* Pass sites data to the table */}
      {!isLoading && (
        <AttendanceTable 
          data={attendanceData} 
          sites={sites} // Pass sites down
          onNotesSubmit={handleNotesSubmit}
          onEditTimesSave={async (payload) => {
            try {
              setIsUpdating(true);
              await editAttendanceTimes(payload);
              await fetchAttendance(selectedDate);
            } catch (err) {
              setError(err.message || 'Failed to save edited times');
            } finally {
              setIsUpdating(false);
            }
          }}
          isLoading={isUpdating}
          error={error && !isLoading ? error : null}
        />
      )}
    </PageContainer>
  );
}

export default AttendanceMain;
